import { doc, getDoc, updateDoc, serverTimestamp, addDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './config';

const COLLECTION = 'subscribers';

// Log unsubscribe event for security/audit purposes
const logUnsubscribeEvent = async (subscriberId, subscriberEmail) => {
  try {
    await addDoc(collection(db, 'securityLogs'), {
      action: 'unsubscribe',
      collection: COLLECTION,
      documentId: subscriberId,
      userId: null,
      userEmail: subscriberEmail,
      before: { status: 'active' },
      after: { status: 'unsubscribed' },
      changedFields: ['status', 'unsubscribedAt'],
      source: 'website_unsubscribe_page',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      timestamp: serverTimestamp()
    });
  } catch (err) {
    // Silent fail for logging
  }
};

export const subscriberService = {
  // Subscribe from website newsletter form
  async subscribe(name, email) {
    try {
      const q = query(collection(db, COLLECTION), where('email', '==', email.toLowerCase().trim()));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const existing = snapshot.docs[0].data();
        if (existing.status === 'active') {
          return { success: true, alreadySubscribed: true };
        }
        // Resubscribe if previously unsubscribed
        await updateDoc(doc(db, COLLECTION, snapshot.docs[0].id), {
          status: 'active',
          name: name || existing.name,
          unsubscribedAt: null,
          unsubscribeReason: null,
          updatedAt: serverTimestamp()
        });
        return { success: true, resubscribed: true };
      }

      await addDoc(collection(db, COLLECTION), {
        email: email.toLowerCase().trim(),
        name: name || '',
        firstName: '',
        lastName: '',
        status: 'active',
        source: 'website',
        tags: [],
        segments: [],
        engagementScore: 50,
        emailsReceived: 0,
        emailsOpened: 0,
        emailsClicked: 0,
        gdprConsent: true,
        gdprConsentDate: serverTimestamp(),
        welcomeSequenceStarted: false,
        welcomeSequenceStep: 0,
        linkedClientId: null,
        subscribedAt: serverTimestamp(),
        unsubscribedAt: null,
        lastEngagedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get subscriber by ID (for unsubscribe verification)
  async getById(id) {
    try {
      const docRef = doc(db, COLLECTION, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          email: data.email,
          name: data.name,
          status: data.status
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting subscriber:', error);
      return null;
    }
  },

  // Unsubscribe a subscriber
  async unsubscribe(id, reason = null) {
    try {
      const subscriber = await this.getById(id);
      if (!subscriber) {
        throw new Error('Subscriber not found');
      }

      if (subscriber.status === 'unsubscribed') {
        return { success: true, alreadyUnsubscribed: true };
      }

      const docRef = doc(db, COLLECTION, id);
      await updateDoc(docRef, {
        status: 'unsubscribed',
        unsubscribedAt: serverTimestamp(),
        unsubscribeReason: reason || 'User clicked unsubscribe link',
        updatedAt: serverTimestamp()
      });

      await logUnsubscribeEvent(id, subscriber.email);

      return { success: true, alreadyUnsubscribed: false };
    } catch (error) {
      console.error('Error unsubscribing:', error);
      throw error;
    }
  }
};

export default subscriberService;
